import integracionesService from '@/services/integracionesService';

/**
 * Credenciales de integraciones por tenant (#351) — el ".env por cliente".
 *
 * Contrato del backend (integraciones_controller.js):
 *   GET    /integraciones/config                       -> { success, data: [...] }
 *   PUT    /integraciones/config/:proveedor/:perfil/:clave -> { success, data, message }
 *   DELETE /integraciones/config/:proveedor/:perfil/:clave -> { success, data, message }
 *   POST   /integraciones/config/:proveedor/:perfil/probar -> { success, data: { ok, ... } }
 *
 * OJO: este módulo NO sigue el envelope { data: { data } } de los ABM de `pagos` — acá es
 * `{ success, data, message }`, sin `title`/`type`. Los errores también difieren: el backend
 * responde `{ success: false, message }` (no `msg`), leído en cada action de abajo.
 *
 * `config` guarda TODA la respuesta de listar (incluye credenciales sin fila propia, resueltas
 * por la cadena de fallback del backend). `guardar`/`eliminar` refetchean el listado completo
 * en vez de mutar la fila local: el catálogo del backend puede reordenar qué credenciales
 * "faltan" según lo que quede activo, y reconstruir ese cómputo acá duplicaría lógica de negocio.
 */

const state = () => ({
  config: [],
  configLoading: false,
  configError: null,
  guardarLoading: false,
  guardarError: null,
  eliminarLoading: false,
  eliminarError: null,
  probarLoading: false,
  probarError: null,
});

const getters = {
  // Agrupa las credenciales planas del backend por integración (proveedor + perfil), en el
  // orden en que llegaron (el backend ya las ordena por proveedor/perfil/clave).
  integracionesAgrupadas: (state) => {
    const agrupadas = [];
    const indice = new Map();

    state.config.forEach((credencial) => {
      const clave = `${credencial.proveedor}::${credencial.perfil}`;
      if (!indice.has(clave)) {
        const grupo = { proveedor: credencial.proveedor, perfil: credencial.perfil, credenciales: [] };
        indice.set(clave, grupo);
        agrupadas.push(grupo);
      }
      indice.get(clave).credenciales.push(credencial);
    });

    return agrupadas;
  },
};

const mensajeError = (error, fallback) => error.response?.data?.message || error.message || fallback;

const actions = {
  async fetchConfig({ commit }, filtros = {}) {
    try {
      commit('SET_CONFIG_LOADING', true);
      const respuesta = await integracionesService.obtenerConfig(filtros);
      commit('SET_CONFIG', respuesta?.data?.data ?? []);
      commit('SET_CONFIG_ERROR', null);
      return respuesta;
    } catch (error) {
      commit('SET_CONFIG_ERROR', mensajeError(error, 'Error al cargar las integraciones'));
      throw error;
    } finally {
      commit('SET_CONFIG_LOADING', false);
    }
  },

  async guardarCredencial({ commit, dispatch }, { proveedor, perfil, clave, valor, id_cuenta, descripcion, activo }) {
    try {
      commit('SET_GUARDAR_LOADING', true);
      const respuesta = await integracionesService.guardarCredencial(proveedor, perfil, clave, {
        valor,
        id_cuenta,
        descripcion,
        activo,
      });
      await dispatch('fetchConfig');
      commit('SET_GUARDAR_ERROR', null);
      return respuesta;
    } catch (error) {
      commit('SET_GUARDAR_ERROR', mensajeError(error, 'Error al guardar la credencial'));
      throw error;
    } finally {
      commit('SET_GUARDAR_LOADING', false);
    }
  },

  async eliminarCredencial({ commit, dispatch }, { proveedor, perfil, clave }) {
    try {
      commit('SET_ELIMINAR_LOADING', true);
      const respuesta = await integracionesService.eliminarCredencial(proveedor, perfil, clave);
      await dispatch('fetchConfig');
      commit('SET_ELIMINAR_ERROR', null);
      return respuesta;
    } catch (error) {
      commit('SET_ELIMINAR_ERROR', mensajeError(error, 'Error al eliminar la credencial'));
      throw error;
    } finally {
      commit('SET_ELIMINAR_LOADING', false);
    }
  },

  async probarConexion({ commit }, { proveedor, perfil }) {
    try {
      commit('SET_PROBAR_LOADING', true);
      const respuesta = await integracionesService.probarConexion(proveedor, perfil);
      commit('SET_PROBAR_ERROR', null);
      return respuesta;
    } catch (error) {
      commit('SET_PROBAR_ERROR', mensajeError(error, 'Error al probar la conexión'));
      throw error;
    } finally {
      commit('SET_PROBAR_LOADING', false);
    }
  },
};

const mutations = {
  SET_CONFIG(s, v) { s.config = v; },
  SET_CONFIG_LOADING(s, v) { s.configLoading = v; },
  SET_CONFIG_ERROR(s, v) { s.configError = v; },
  SET_GUARDAR_LOADING(s, v) { s.guardarLoading = v; },
  SET_GUARDAR_ERROR(s, v) { s.guardarError = v; },
  SET_ELIMINAR_LOADING(s, v) { s.eliminarLoading = v; },
  SET_ELIMINAR_ERROR(s, v) { s.eliminarError = v; },
  SET_PROBAR_LOADING(s, v) { s.probarLoading = v; },
  SET_PROBAR_ERROR(s, v) { s.probarError = v; },
};

export default { namespaced: true, state, getters, actions, mutations };
